<script lang="ts" setup>
import { countries } from '@/models/country.model';
import {
  DataTable,
  nameRules,
  dataRules,
  DataTableLegend,
} from '@/models/data-table.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { DataTablesActions, DataTablesState } from '@/stores/data-tables.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, defineProps } from 'vue';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState & { ds: DataTablesState }>();
const defaults = computed<DataTable[]>(() => store.state.ds.defaults);
const legends = computed<DataTableLegend[]>(() => store.state.ds.legends);

const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<DataTable>,
  },
});

const valid = ref<boolean>(false);

const dataTable = ref<DataTable>({
  id: NEW_ID,
  name: '',
  country: 'nl',
  data: '{}',
});

const legend = computed<DataTableLegend>(
  () =>
    legends.value.find((v) => v.name === dataTable.value.name) || {
      name: '',
      data: {},
    },
);

const action = computed<string>(() =>
  dataTable.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveDataTable = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (dataTable.value.id === NEW_ID) {
      success = await store.dispatch(
        DataTablesActions.AddDataTable,
        dataTable.value,
      );
    } else {
      success = await store.dispatch(
        DataTablesActions.UpdateDataTable,
        dataTable.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved Data Table',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(DataTablesActions.GetDataTables);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Data Table',
      type: 'error',
    });
  }
};

onMounted(() => {
  if (props.data) {
    Object.assign(dataTable.value, {
      ...props.data,
      data: JSON.stringify(props.data.data, null, 2),
    });
  }

  store.dispatch(DataTablesActions.GetDataTablesDefaults);
  store.dispatch(DataTablesActions.GetDataTablesLegends);
});

const useData = (d: string) => {
  Object.assign(dataTable.value, {
    data: JSON.stringify(d, null, 2),
  });
};

const removeLabel = async () => {
  if (
    dataTable.value &&
    dataTable.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Data Table"?')
  ) {
    store.dispatch(DataTablesActions.DeleteDataTable, dataTable.value.id);

    store.commit(Mutations.HideDrawer);
  }
};
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveDataTable">
    <v-container>
      <h2>{{ $t(action) }} a data table</h2>
      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model="dataTable.name"
            :rules="nameRules"
            label="Name"
            required
          ></v-text-field>
        </v-col>
        <v-col class="mx-auto" cols="5">
          <v-autocomplete
            v-model="dataTable.country"
            :items="countries"
            label="Country"
            item-title="text"
            item-value="value"
            :hide-details="true"
          ></v-autocomplete>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <v-textarea
            v-model="dataTable.data"
            required
            rows="20"
            :rules="dataRules"
          ></v-textarea>
        </v-col>
        <v-col cols="2">
          <v-btn
            density="compact"
            class="mb-2"
            type="button"
            @click="useData(def.data)"
            block
            color="warning"
            v-for="def in defaults"
            :key="def.id"
            >Use {{ def.name }}</v-btn
          >
        </v-col>
      </v-row>

      <div v-if="legend.name && dataTable.name">
        <h3>Legend</h3>
        <v-row>
          <v-col v-for="key in Object.keys(legend.data || {})" :key="key">
            <h4>{{ key }}</h4>
            <pre>{{ JSON.stringify(legend.data[key], null, 2) }}</pre>
          </v-col>
        </v-row>
      </div>

      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            @click="removeLabel"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
pre {
  font-family: sans-serif;
  font-size: 12px;
  background-color: rgb(255, 230, 171);
  display: block;
  height: 100%;
  padding: 8px;
}
</style>
